import React, { useEffect, useState } from "react";
import Dashboard from "./dashboard";
import axios from "../api/baseurl";
import CreateTestsForPackage from "./addTestPackages";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import TestCsvFile from "./testsCsvFile";
import TestsCsvFormat from "./testCsvFormat";
import { BiEditAlt } from "react-icons/bi";
import UpdateTestCostModal from "./updateTestCostModal";
import { FiInfo } from "react-icons/fi";
import { Modal, Button } from "react-bootstrap";
import "./hm-theme.css";

function TestPackages() {
  const [testPackages, setTestPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateCostModalOpen, setIsUpdateCostModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loader, setLoader] = useState(true);
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isCostHistoryModalOpen, setIsCostHistoryModalOpen] = useState(false);
  const [costHistory, setCostHistory] = useState([]);

  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data")).token;

  useEffect(() => {
    getData();
    getCategories();
  }, []);

  const getData = async () => {
    setLoader(true);
    try {
      const response = await axios.get(`/api/labtest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestPackages(response.data || []);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getCategories = async () => {
    try {
      const response = await axios.get(`/api/testcategory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data || []);
    } catch (error) {
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const handleCreateCompany = () => {
    setSelectedTest(null);
    setIsModalOpen(true);
  };

  const handleEditTest = (test) => {
    setSelectedTest(test);
    setIsModalOpen(true);
  };

  const handleUpdateCost = (testId) => {
    setSelectedTest(testId);
    setIsUpdateCostModalOpen(true);
  };

  const handleCloseUpdateCostModal = () => {
    setIsUpdateCostModalOpen(false);
    setSelectedTest(null);
    getData();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTest(null);
    getData();
  };

  const handleCostHistory = (test) => {
    setSelectedTest(test);
    setIsCostHistoryModalOpen(true);
    fetchCostHistory(test.id);
  };

  const fetchCostHistory = async (testId) => {
    setLoader(true);
    try {
      const response = await axios.get(`/api/labTestCost/testId=${testId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCostHistory(response.data || []);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handleToggleLabMandatory = async (test) => {
    const newValue = !test.labMandatory;
    // Optimistic update
    setTestPackages((prev) =>
      prev.map((t) => (t.id === test.id ? { ...t, labMandatory: newValue } : t))
    );
    try {
      await axios.post(
        `/api/labtest/update`,
        {
          id: test.id,
          testname: test.testname,
          description: test.description,
          categoryid: test.categoryid,
          labMandatory: newValue,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Lab Mandatory ${newValue ? "enabled" : "disabled"} for ${test.testname}`);
    } catch (error) {
      // Revert on failure
      setTestPackages((prev) =>
        prev.map((t) => (t.id === test.id ? { ...t, labMandatory: !newValue } : t))
      );
      handleError(error);
    }
  };

  const filterTestPackages = (packages) => {
    let filtered = packages;
    if (selectedCategory) {
      filtered = filtered.filter((pkg) => pkg.testCat?.categoryname === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter((pkg) => (pkg.testname || "").toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return filtered;
  };

  const filteredList = filterTestPackages(testPackages);

  return (
    <div>
      <Dashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">science</i>
                Diagnostic Tests Catalog
              </h2>
              <p className="hm-page-subtitle">Manage individual lab tests, cost histories, and test categories</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-outline-primary rounded-pill px-3" onClick={() => setShowImageModal(true)}>
                Format Guide
              </button>
              <button className="btn btn-outline-secondary rounded-pill px-3" onClick={() => setIsCsvUploadModalOpen(true)}>
                Upload CSV
              </button>
              <button className="btn-hm-primary" onClick={handleCreateCompany}>
                <i className="material-icons" style={{ fontSize: "1rem" }}>add</i>
                Add Test
              </button>
            </div>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_alt</i>
                Search & Category Filter
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Search Test</label>
                  <div className="position-relative">
                    <input
                      type="search"
                      className="form-control ps-4"
                      placeholder="Search test name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                      search
                    </i>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Filter by Category</label>
                  <select
                    className="form-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.categoryname}>
                        {category.categoryname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">biotech</i>
                Lab Tests List
              </h3>
              <span className="hm-badge hm-badge-primary">{filteredList.length} tests</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Test Name</th>
                      <th>Description</th>
                      <th>Cost</th>
                      <th>Lab Mandatory</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.length === 0 ? (
                      <tr>
                        <td colSpan="7">
                          <div className="hm-empty-state">
                            <i className="material-icons">science</i>
                            <p>{loader ? "Loading tests..." : "No tests found"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredList.map((test, index) => (
                        <tr key={test.id}>
                          <td>{index + 1}</td>
                          <td>
                            <span className="hm-badge hm-badge-info">{test.testCat?.categoryname || "General"}</span>
                          </td>
                          <td className="fw-semibold">{test.testname}</td>
                          <td className="small text-muted">{test.description || "—"}</td>
                          <td className="fw-bold">
                            ₹ {test.testCost?.cost || 0}
                            {test.testCost?.cost > 0 && (
                              <FiInfo
                                onClick={() => handleCostHistory(test)}
                                style={{ cursor: "pointer", marginLeft: "6px", color: "#2cb6e9" }}
                                title="View Cost History"
                              />
                            )}
                          </td>
                          <td>
                            <div
                              style={{ width: "44px", height: "24px", cursor: "pointer", position: "relative", display: "inline-block" }}
                              onClick={() => handleToggleLabMandatory(test)}
                              title={test.labMandatory ? "Click to disable Lab Mandatory" : "Click to enable Lab Mandatory"}
                            >
                              <div style={{
                                width: "44px", height: "24px", borderRadius: "12px",
                                background: test.labMandatory ? "#2f3a88" : "#dee2e6",
                                transition: "background 0.25s", position: "relative",
                              }}>
                                <div style={{
                                  width: "18px", height: "18px", borderRadius: "50%", background: "#fff",
                                  position: "absolute", top: "3px",
                                  left: test.labMandatory ? "23px" : "3px",
                                  transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                }} />
                              </div>
                            </div>
                            <span
                              className="ms-2 small fw-semibold"
                              style={{ color: test.labMandatory ? "#2f3a88" : "#94a3b8", fontSize: "0.72rem" }}
                            >
                              {test.labMandatory ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-outline-primary btn-sm rounded-pill py-0 px-2"
                                style={{ fontSize: "0.78rem" }}
                                onClick={() => handleUpdateCost(test.id)}
                              >
                                Update Cost
                              </button>
                              <button
                                className="btn btn-link text-dark p-0 border-0"
                                onClick={() => handleEditTest(test)}
                                title="Edit Test"
                              >
                                <BiEditAlt size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>

      <CreateTestsForPackage
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        test={selectedTest}
      />
      <TestsCsvFormat isOpen={showImageModal} onRequestClose={() => setShowImageModal(false)} />
      <TestCsvFile isOpen={isCsvUploadModalOpen} onRequestClose={() => { setIsCsvUploadModalOpen(false); getData(); }} />
      <UpdateTestCostModal
        isOpen={isUpdateCostModalOpen}
        onRequestClose={handleCloseUpdateCostModal}
        testId={selectedTest}
      />

      {/* Cost History Modal */}
      <Modal show={isCostHistoryModalOpen} onHide={() => setIsCostHistoryModalOpen(false)} centered>
        <Modal.Header closeButton className="hm-card-header">
          <Modal.Title className="hm-card-title">
            Cost History — {selectedTest?.testname}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {costHistory.length === 0 ? (
            <p className="text-muted text-center my-3">No cost history available</p>
          ) : (
            <table className="hm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {costHistory.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.date}</td>
                    <td className="fw-bold">₹ {item.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsCostHistoryModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TestPackages;